import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './user-menu.css';

export type UserMenuItem = {
  id: string;
  label: string;
  href: string;
  testId?: string;
};

export interface UserMenuProps {
  userName: string;
  items: UserMenuItem[];
  onLogout: () => void;
}

type DropdownPosition = { top: number; right: number };

export function UserMenu({ userName, items, onLogout }: UserMenuProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const totalItems = items.length + 1; // +1 for logout button

  const open = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen(true);
    setFocusedIndex(0);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
    triggerRef.current?.focus();
  }, []);

  const handleItemClick = useCallback((item: UserMenuItem) => {
    setIsOpen(false);
    navigate(item.href);
  }, [navigate]);

  const handleLogoutClick = useCallback(() => {
    setIsOpen(false);
    onLogout();
  }, [onLogout]);

  // Focus the active menu item when focusedIndex changes
  useEffect(() => {
    if (isOpen && focusedIndex >= 0) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [isOpen, focusedIndex]);

  // Click-outside to close
  useEffect(() => {
    if (!isOpen) return;

    function handleMouseDown(event: MouseEvent) {
      const target = event.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // Keyboard navigation on the trigger
  const handleTriggerKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen) { close(); } else { open(); }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) open();
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(totalItems - 1);
        }
        break;
    }
  }, [isOpen, open, close, totalItems]);

  // Keyboard navigation inside the menu
  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev - 1 + totalItems) % totalItems);
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(totalItems - 1);
        break;
      case 'Tab':
        close();
        break;
    }
  }, [close, totalItems]);

  return (
    <div className="user-menu" data-testid="experience-user-menu">
      <button
        ref={triggerRef}
        className="user-menu-trigger"
        data-testid="experience-user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => isOpen ? close() : open()}
        onKeyDown={handleTriggerKeyDown}
      >
        {/* Inline color:inherit escapes the broad `[data-theme] span { color: var(--main-text) }`
            rule (specificity 0,3,1, intentionally non-!important) so the label follows the
            button's --header-text — white on the navy host bar, theme color elsewhere. */}
        <span style={{ color: 'inherit' }}>{userName}</span>
        <svg
          className="user-menu-chevron"
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
          aria-hidden="true"
        >
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isOpen && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="user-menu-dropdown"
          role="menu"
          aria-label="User menu"
          data-testid="experience-user-menu-dropdown"
          onKeyDown={handleMenuKeyDown}
          style={position ? { top: position.top, right: position.right } : undefined}
        >
          {items.map((item, index) => (
            <a
              key={item.id}
              ref={el => { itemRefs.current[index] = el; }}
              href={item.href}
              role="menuitem"
              tabIndex={-1}
              className="user-menu-item"
              data-testid={item.testId}
              onClick={(e) => {
                e.preventDefault();
                handleItemClick(item);
              }}
            >
              {item.label}
            </a>
          ))}

          <hr className="user-menu-divider" role="separator" />

          <button
            ref={el => { itemRefs.current[items.length] = el; }}
            role="menuitem"
            tabIndex={-1}
            className="user-menu-item"
            data-testid="experience-user-menu-logout"
            onClick={handleLogoutClick}
          >
            Sign out
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}
